import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { designSystem } from '@yola/ws-ui';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import yodomains from '@yola/yodomainsjs';
import get from 'lodash.get';
import bowser from 'yola-bowser';
import { constants } from '@yola/subscription-manager-js';
import yoproductsServiceClient from '../common/helpers/yoproducts-service-client';
import configStorage from '../configuration/config-storage';
import { ENTER_KEY, viewMode, extraPrices } from './constants/common';
import actions from './actions';
import useDomainsState from './hooks/use-domains-state';
import useHandlers from './hooks/use-handlers';
import getAutocompleteOptions from './helpers/get-autocomplete-options';
import generatePaymentUrl from '../utils/generate-payment-url';
import processDomainsData from './helpers/process-domains-data';
import getCaptions from './helpers/get-captions';
import getSpecialOfferCaptions from './helpers/get-special-offer-captions';
import processFreeSubdomainData from './helpers/process-free-subdomain-data';
import getDomainParts from '../common/helpers/get-domains-parts';
import PurchasedDomainsSelectorContainer from './containers/purchased-domains-selector-container';
import tabIds from './constants/tab-ids';
import useMatchBreakpoint from '../common/hooks/use-match-breakpoint';
import breakpoints from '../common/constants/breakpoints';

const { DOMAIN_SUBSCRIPTION } = constants.SubscriptionType;

const {
  DomainSelector,
  DomainSelectorErrorMessage,
  DomainSelectorListRow,
  DomainSelectorSpecialOffer,
  Paragraph,
  Heading,
  Container,
  Row,
  Col,
  SectionContent,
  SectionHeader,
  Section,
  ButtonsHeader,
  Box,
  Tabs,
  TabsList,
  Tab,
  TabsContentList,
  TabContent,
} = designSystem;

const defaultCaptionsOverrides = {};
const defaultAdditionalPaymentParams = {};

function B2CDomainSelector({
  appElementSelector = 'body',
  currency = 'USD',
  siteId = null,
  onSearchPerformed = Function.prototype,
  onDomainSearchFailure,
  onSelect,
  onSearchStart = Function.prototype,
  onDomainFieldChange = Function.prototype,
  domainName = null,
  onClose = null,
  view = viewMode.DIALOG,
  onNext = null,
  captionsOverrides = defaultCaptionsOverrides,
  additionalPaymentParams = defaultAdditionalPaymentParams,
  withFreeSubdomain = false,
  specialOffer = null,
  activeSubscriptionTerm = '',
  extraPrice = '',
  onFreeSubdomainSelect = Function.prototype,
  purchasedDomainsData = null,
  isUpgradeToPublishEnabled = false,
  onPurchasedDomainSelect = Function.prototype,
  onTabChange = Function.prototype,
  onSpecialOfferSwitch = Function.prototype,
  onConnectThirdPartyDomainClick = Function.prototype,
}) {
  const {
    products = [],
    initiator,
    term,
    backUrl,
    successUrl,
    failUrl,
    closeUrl,
  } = additionalPaymentParams;
  const canGetDomainForFree = specialOffer?.disabled;
  const [isSpecialOfferEnabled, setIsSpecialOfferEnabled] = useState(canGetDomainForFree);
  const [activeTabId, setActiveTabId] = useState(tabIds.DOMAIN_PURCHASE);

  const [state, dispatch] = useDomainsState({
    domainName,
    currency,
  });

  const timerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const handlersRef = useHandlers({
    onDomainSearchFailure,
    onSearchPerformed,
    onSearchStart,
    onSelect,
  });

  const isSmBreakpoint = useMatchBreakpoint(breakpoints.SM);
  const isLoading = Boolean(
    state.isTldCollectionLoading || state.areDomainsLoading || state.arePricesLoading
  );
  const shouldBundleSubscription = isSpecialOfferEnabled && specialOffer?.bundled;

  const onInputChange = useCallback(
    (value) => {
      dispatch(actions.setInputValue(value, { resetValidationResult: true }));
    },
    [dispatch]
  );

  const handleSearch = useCallback(
    (options = {}) => {
      dispatch(actions.getDomains(options));
    },
    [dispatch]
  );

  const handleUserSearchRequest = useCallback(() => {
    handleSearch({ userAction: true });
  }, [handleSearch]);

  const onBlur = useCallback(() => {
    dispatch(actions.validateValue());
  }, [dispatch]);

  const onKeyDown = useCallback(
    (event) => {
      if (!isLoading && event.key === ENTER_KEY) {
        event.target.blur();
        setTimeout(handleUserSearchRequest);
      }
    },
    [handleUserSearchRequest, isLoading]
  );

  const onChange = useCallback(
    (data) => {
      const { label: value = '' } = data || {};

      onDomainFieldChange(data);

      dispatch(actions.setInputValue(value));
    },
    [dispatch, onDomainFieldChange]
  );

  const handleSpecialOfferSwitch = useCallback(
    () =>
      setIsSpecialOfferEnabled((prevState) => {
        onSpecialOfferSwitch(!prevState);
        return !prevState;
      }),
    [onSpecialOfferSwitch]
  );

  const handleFreeSubdomainClick = useCallback(() => {
    onFreeSubdomainSelect({
      searchString: state.searchString,
    });
  }, [onFreeSubdomainSelect, state.searchString]);

  const loadAutocompleteOptions = useCallback(
    (inputText, resolve) => {
      clearTimeout(timerRef.current);

      if (inputText.trim().length < 3) {
        resolve([]);
        return;
      }

      const { domain } = getDomainParts(inputText);

      if (state.autocompleteOptionPrefix === domain) {
        resolve(state.autocompleteOptions);
        return;
      }

      timerRef.current = setTimeout(() => {
        const options = getAutocompleteOptions(state.tldCollection, domain);

        resolve(options);
        dispatch(actions.setAutocompleteData(domain, options));
      }, 500);
    },
    [state.autocompleteOptions, state.autocompleteOptionPrefix, state.tldCollection, dispatch]
  );

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTabId(tabId);
      onTabChange(tabId);
    },
    [onTabChange]
  );

  const handleEmptyStateButtonClick = useCallback(() => {
    handleTabChange(tabIds.DOMAIN_PURCHASE);
  }, [handleTabChange]);

  // Get prices data on the component init
  useEffect(() => {
    const yoproducts = yoproductsServiceClient.get();

    dispatch(actions.getPrices());

    const params = {};

    if (extraPrice) {
      params.extra_prices = extraPrice;
    }

    yoproducts.requests
      .productsTree(null, params)
      .then((data) => dispatch(actions.setPrices(data.domain, extraPrice)));
  }, [dispatch, extraPrice]);

  useEffect(() => {
    const { commonConfig } = configStorage.get();
    const options = {
      apiUrl: get(commonConfig, 'public-api-gateway.url', ''),
    };

    dispatch(actions.getTldCollection());

    yodomains.requests
      .tldsCollection(options)
      .then((response) => {
        const { data } = response;
        const tldList = Object.keys(data);

        return tldList.filter((tld) => data[tld].enabled);
      })
      .then((availableTldCollection) => {
        dispatch(actions.setTldCollection(availableTldCollection.sort()));
      });
  }, [dispatch]);

  // Get the new domains list when search query changes
  useEffect(() => {
    const { searchString, domainsRequestOptions } = state;

    if (searchString) {
      abortControllerRef.current = new AbortController();
      const { commonConfig } = configStorage.get();
      const options = {
        apiUrl: get(commonConfig, 'public-api-gateway.url', ''),
        searchString,
        signal: abortControllerRef.current.signal,
      };

      handlersRef.current.onSearchStart({
        searchString,
        domainsRequestOptions,
      });

      const fetchDomains = async (fetchOptions) => {
        const response = await yodomains.requests.lookupDomainsAvailability(fetchOptions);
        const { results, search_key: searchKey } = response;
        const availableDomains = results.filter(
          (domainItem) => domainItem.available || domainItem.domain === searchString
        );
        const isSearchCompleted = !searchKey;

        if (isSearchCompleted) {
          handlersRef.current.onSearchPerformed({
            searchString,
            searchResults: results,
            domainsRequestOptions,
          });
          dispatch(actions.setDomains(availableDomains));
          return;
        }

        if (availableDomains.length) {
          dispatch(actions.setDomains(availableDomains));
        }

        await fetchDomains({
          ...options,
          searchKey,
        });
      };

      fetchDomains(options).catch((error) => {
        if (error.name !== 'AbortError') {
          handlersRef.current.onDomainSearchFailure();
        }
      });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [state.searchString, dispatch]);

  useEffect(() => {
    // load domains in case when default domain name is passed
    handleSearch({ userAction: false });
  }, [handleSearch]);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    []
  );

  const domainList = useMemo(() => {
    function handleDomainSelect(item) {
      const { domain } = item;

      const paymentParams = {
        products: [DOMAIN_SUBSCRIPTION, ...products],
        domain_name: domain,
        ...(siteId && { latitude_site_id: siteId }),
        ...(initiator && { initiator }),
        ...(term && { term }),
        ...(backUrl && { back_url: backUrl }),
        ...(successUrl && { success_url: successUrl }),
        ...(failUrl && { fail_url: failUrl }),
        ...(closeUrl && { close_url: closeUrl }),
        p1y_upgrade: true,
      };

      const paymentUrl = generatePaymentUrl(paymentParams);

      handlersRef.current.onSelect({
        paymentUrl,
        domain,
        searchString: state.searchString,
        shouldBundleSubscription,
      });
    }

    const domainsData = processDomainsData({
      prices: state.prices,
      domains: state.domains,
      currency,
      isSpecialOfferEnabled,
      extraPrice,
    });

    return (
      domainsData &&
      domainsData.map((item) => (
        <DomainSelectorListRow
          captions={item.captions}
          disabled={item.disabled}
          buttonAppearance={item.buttonAppearance}
          priceAppearance={item.priceAppearance}
          key={item.domain}
          isLoading={isLoading}
          onClick={() => handleDomainSelect(item)}
        />
      ))
    );
  }, [
    state.domains,
    state.searchString,
    state.prices,
    currency,
    siteId,
    products,
    term,
    initiator,
    backUrl,
    successUrl,
    failUrl,
    closeUrl,
    handlersRef,
    isLoading,
    isSpecialOfferEnabled,
    extraPrice,
    shouldBundleSubscription,
  ]);

  const freeSubdomainRow = useMemo(() => {
    if (!state.searchString || !withFreeSubdomain || isLoading) {
      return null;
    }

    const freeSubdomainData = processFreeSubdomainData({
      isUpgradeToPublishEnabled,
      isSpecialOfferEnabled,
      canGetDomainForFree,
      activeSubscriptionTerm,
    });

    return <DomainSelectorListRow {...freeSubdomainData} onClick={handleFreeSubdomainClick} />;
  }, [
    isLoading,
    canGetDomainForFree,
    isSpecialOfferEnabled,
    activeSubscriptionTerm,
    handleFreeSubdomainClick,
    isUpgradeToPublishEnabled,
    withFreeSubdomain,
    state.searchString,
  ]);

  const captions = useMemo(
    () => getCaptions(state.searchString, captionsOverrides),
    [state.searchString, captionsOverrides]
  );

  const specialOfferCaptions = useMemo(
    () =>
      specialOffer && state.comDomainDiscount
        ? getSpecialOfferCaptions({
            discount: state.comDomainDiscount,
            activeSubscriptionTerm,
            canGetDomainForFree,
            currency,
          })
        : null,
    [activeSubscriptionTerm, specialOffer, state.comDomainDiscount, currency, canGetDomainForFree]
  );

  const prevButton = useMemo(() => {
    if (!onClose) {
      return null;
    }

    return {
      onClick: onClose,
      label: captions.backButton,
      iconGlyph: 'back',
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [onClose]);

  const nextButton = useMemo(() => {
    if (!onNext) {
      return null;
    }

    return {
      onClick: () => onNext(),
      label: captions.nextButton,
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [onNext]);

  const shouldShowWarning = useMemo(() => {
    if (state.areDomainsLoading || !state.domains || !state.searchString) return false;

    const targetDomain = state.domains.find(
      (domain) => domain.domain === state.searchString.toLowerCase()
    );

    return Boolean(targetDomain && !targetDomain.available);
  }, [state.areDomainsLoading, state.domains, state.searchString]);

  const domainSelectorContent = (
    <DomainSelector
      errorMessage={
        !state.inputValue || state.isInputValueValid ? null : captions.search.errorMessage
      }
      captions={{
        search: {
          button: captions.search.button,
          placeholder: captions.search.placeholder,
        },
      }}
      loadAutocompleteOptions={loadAutocompleteOptions}
      onInput={onInputChange}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onButtonClick={handleUserSearchRequest}
      isLoading={isLoading}
      defaultInputValue={state.inputValue}
    >
      {shouldShowWarning && (
        <DomainSelectorErrorMessage>
          <Paragraph>
            <span
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{ __html: captions.errorMessage }}
            />
          </Paragraph>
        </DomainSelectorErrorMessage>
      )}
      {specialOfferCaptions && (
        <Box marginTop="spacing-m">
          <DomainSelectorSpecialOffer
            captions={specialOfferCaptions}
            checked={isSpecialOfferEnabled}
            disabled={specialOffer?.disabled}
            {...(canGetDomainForFree && { tagIconGlyph: 'gift' })}
            onToggle={handleSpecialOfferSwitch}
          />
        </Box>
      )}
      <Box marginTop="spacing-3-xs">
        {domainList}
        {freeSubdomainRow}
      </Box>
    </DomainSelector>
  );

  const content = (
    <div className="ws-b2c-domain-selector">
      {(onClose || onNext) && (
        <div className="ws-b2c-domain-selector__header">
          <ButtonsHeader
            isSticky={Boolean(bowser.mobile || bowser.tablet)}
            prevButton={prevButton}
            nextButton={nextButton}
          />
        </div>
      )}
      <div
        className={classNames('ws-b2c-domain-selector__main', {
          'ws-b2c-domain-selector__main--centered': !purchasedDomainsData,
        })}
      >
        <Container fluid="md">
          <Row className="justify-content-center g-0">
            <Col xxl={6} xl={6} lg={8} md={10} sm={10}>
              <Section>
                <SectionHeader>
                  <Heading align="center" type="heading-1">
                    {captions.title}
                  </Heading>
                  <Paragraph align="center" size="large">
                    <strong>{captions.description}</strong>
                  </Paragraph>
                </SectionHeader>
                <SectionContent>
                  {!purchasedDomainsData && domainSelectorContent}
                  {!!purchasedDomainsData && (
                    <Tabs
                      noBackground
                      noSideSpacing
                      noVerticalSpacing
                      skipRenderInactiveContent
                      disableSwipeableContent
                      noOverflow
                      activeTabId={activeTabId}
                      onTabChange={handleTabChange}
                    >
                      <TabsList {...(isSmBreakpoint && { variant: 'fullWidth' })}>
                        <Tab label={captions.tabs.domainsTitle} tabId={tabIds.DOMAIN_PURCHASE} />
                        <Tab
                          label={captions.tabs.purchasedDomainsTitle}
                          tabId={tabIds.DOMAIN_POINTING}
                        />
                      </TabsList>
                      <TabsContentList>
                        <TabContent tabId={tabIds.DOMAIN_PURCHASE}>
                          <Box marginTop="spacing-xl">{domainSelectorContent}</Box>
                        </TabContent>
                        <TabContent tabId={tabIds.DOMAIN_POINTING}>
                          <Box marginTop="spacing-xl">
                            <PurchasedDomainsSelectorContainer
                              domains={purchasedDomainsData?.domains}
                              onDomainSelect={onPurchasedDomainSelect}
                              onConnectThirdPartyDomainClick={onConnectThirdPartyDomainClick}
                              isLoading={purchasedDomainsData?.isLoading}
                              onEmptyStateButtonClick={handleEmptyStateButtonClick}
                            />
                          </Box>
                        </TabContent>
                      </TabsContentList>
                    </Tabs>
                  )}
                </SectionContent>
              </Section>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );

  if (view === viewMode.CONTENT) {
    return content;
  }

  // Hide other page content from screen readers
  ReactModal.setAppElement(appElementSelector);

  return (
    <ReactModal
      isOpen
      className="ws-b2c-domain-selector-dialog"
      overlayClassName={{
        base: 'wp-dialog-overlay wp-dialog-overlay--fade wp-dialog-overlay--without-text-styles',
        afterOpen: 'wp-dialog-overlay--after-open',
        beforeClose: 'wp-dialog-overlay--before-close',
      }}
    >
      {content}
    </ReactModal>
  );
}

B2CDomainSelector.propTypes = {
  currency: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
  onDomainSearchFailure: PropTypes.func.isRequired,
  onSearchStart: PropTypes.func,
  onSearchPerformed: PropTypes.func,
  onDomainFieldChange: PropTypes.func,
  appElementSelector: PropTypes.string,
  siteId: PropTypes.string,
  domainName: PropTypes.string,
  view: PropTypes.oneOf([viewMode.DIALOG, viewMode.CONTENT]),
  captionsOverrides: PropTypes.shape({
    closeButton: PropTypes.string,
    nextButton: PropTypes.string,
  }),
  additionalPaymentParams: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.string),
    term: PropTypes.string,
    initiator: PropTypes.string,
    backUrl: PropTypes.string,
    successUrl: PropTypes.string,
    failUrl: PropTypes.string,
    closeUrl: PropTypes.string,
  }),
  withFreeSubdomain: PropTypes.bool,
  onFreeSubdomainSelect: PropTypes.func,
  purchasedDomainsData: PropTypes.shape({
    isLoading: PropTypes.bool,
    domains: PropTypes.arrayOf(
      PropTypes.shape({ domain: PropTypes.string, available: PropTypes.bool })
    ),
  }),
  onPurchasedDomainSelect: PropTypes.func,
  onTabChange: PropTypes.func,
  specialOffer: PropTypes.oneOfType([
    PropTypes.shape({
      bundled: PropTypes.bool,
      subscriptionType: PropTypes.string,
      disabled: PropTypes.bool,
    }),
    PropTypes.oneOf([null]),
  ]),
  extraPrice: PropTypes.oneOf([extraPrices.FREE_COM_DOMAIN, extraPrices.NO_DISCOUNTS]),
  activeSubscriptionTerm: PropTypes.string,
  isUpgradeToPublishEnabled: PropTypes.bool,
  onSpecialOfferSwitch: PropTypes.func,
  onConnectThirdPartyDomainClick: PropTypes.func,
};

export default B2CDomainSelector;
